import { usePartner } from '@/context/PartnerContext'
import { Box, Button, Center, Divider, Grid, Heading, Icon, List, ListItem, Spinner, Stack, Text } from '@chakra-ui/react'
import { faCheckCircle, faCircleXmark } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AuthPageLayout } from 'BrokerTsLib/src/components'
import { ResetPasswordForm, ResetPasswordUserInfo, SendResetPasswordLinkForm } from 'brokertslib/src/containers'
import type { ResetPasswordInfo } from 'BrokerTsLib/src/dtos'
import type { PasswordRule } from 'BrokerTsLib/src/hooks'
import { useGetResetPasswordInfo } from 'BrokerTsLib/src/hooks'
import type { ReactNode } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const userId = searchParams.get('userId') ?? undefined
  const token = searchParams.get('token') ?? undefined
  const isNewUser = !!searchParams.get('isNewUser')

  const { info, isLoading, isError, passwordRules } = useGetResetPasswordInfo(userId, token)

  const [isFinished, setIsFinished] = useState(false)

  const state = useMemo(() => {
    if (!userId || !token) return 'request'

    if (isLoading) return 'loading'

    if (isError) return 'error'

    if (isFinished) return 'finished'

    return 'reset'
  }, [userId, token, isLoading, isError, isFinished])

  const { title, subtitle } = GetTitleAndSubtitle({ isNewUser, state })
  
  const { currentPartner } = usePartner()

  return <AuthPageLayout title={title} subtitle={subtitle} logo={currentPartner?.logo}>
    <resetPasswordPageContext.Provider value={{ userId, token, isNewUser, info, isLoading, isError, passwordRules, isFinished, setIsFinished, state }}>
      <ResetPasswordLayout>
        {state === 'request' && <ResetPasswordRequest />}

        {state === 'loading' && <ResetPasswordLoading />}

        {state === 'reset' && <ResetPasswordReset />}

        {state === 'finished' && <ResetPasswordFinished />}

        {state === 'error' && <ResetPasswordError />}
      </ResetPasswordLayout>

    </resetPasswordPageContext.Provider>
  </AuthPageLayout>
}

interface ResetPasswordPageContext {
  userId: string | undefined,
  token: string | undefined,
  isNewUser: boolean,
  info: ResetPasswordInfo | undefined,
  isLoading: boolean,
  isError: boolean,
  passwordRules: PasswordRule[],
  isFinished: boolean,
  setIsFinished: (value: boolean) => void
  state: 'request' | 'loading' | 'reset' | 'finished' | 'error'
}

const resetPasswordPageContext = createContext<ResetPasswordPageContext | null>(null)

const useResetPasswordContext = () => {
  const context = useContext(resetPasswordPageContext)
  if (!context) {
    throw new Error('ResetPasswordLayout must be used within ResetPasswordPageContext')
  }
  return context
}

const ResetPasswordRequest = () => {
  return <Stack mt='auto'>
    <SendResetPasswordLinkForm w='full' />
    <Text variant='textMuted' fontSize='md' textAlign='left'>
      Na Váš e-mail přijde odkaz pro reset hesla.<br />
      Pokud neznáte své uživatelské jméno, kontaktujte uživatelskou podporu.
    </Text>
  </Stack>
}

const GetTitleAndSubtitle = ({ isNewUser, state }: { isNewUser: boolean, state: string }) => {
  return state === 'request' ? 
    {
      title: isNewUser ? 'Nastavení hesla' : 'Obnovení hesla', 
      subtitle: isNewUser ? <>Nastavte si své heslo<br /> pro přihlášení do aplikace.</> : <>Zadejte své uživatelské jméno<br /> pro obnovení hesla.</>
    } 
    : state === 'error' ?
      {
        title: 'Neplatný odkaz', 
        subtitle: <>Váš odkaz pro {isNewUser ? 'nastavení hesla' : 'změnu hesla'} <br /> není platný nebo vypršel.</>
      }
      :
      {
        title: 'Změna hesla', 
        subtitle: <>Zadejte své nové heslo.</>
      }
}

const ResetPasswordLayout = ({ children }: { children: ReactNode }) => {
  const { state, passwordRules } = useResetPasswordContext()

  return <Grid 
    gridTemplateColumns={{ base: 'auto', xl: state === 'reset' ? 'minmax(50%, 60ch) minmax(50%, 60ch)' : 'minmax(100%, 60ch)' }} 
    gap={{ base: 2, md: 8 }}
    maxW={state === 'reset' ? '2xl' : 'xs'}
    mx='auto'
  >
    <Stack>
      <Box flexGrow={1} alignContent='center'>
        <Stack h='full' gap={6}>
          {children}
        </Stack>
      </Box>
    </Stack>

    {state === 'reset' &&
            <Stack >
              <Heading sx={{ fontSize: 'xl', fontWeight: 'bold', textAlign: { base: 'center', md: 'left' }, mb: 2 }}>
                Pravidla pro heslo
              </Heading>
              <List sx={{ fontSize: 'lg', fontWeight: 'medíum', textAlign: { base: 'center', md: 'left' }, mb: 2 }} listStyleType='disc' id="password-constraints">
                {passwordRules.map(x => <ListItem key={x.key}>{x.name}</ListItem>)}
              </List>
            </Stack>
    }
  </Grid>
}

const ResetPasswordLoading = () => <>
  <Stack h='full' gap={6}>
    <Center flexGrow={1} as={Stack} py={{ base: 4, md: 6 }} gap={{ base: 4, md: 6 }}>
      <Spinner size='xl'
        emptyColor="neutral.200" speed="0.7s" />
      <Heading fontSize='2xl' fontWeight='medium'>
        Načítání informací...
      </Heading>
    </Center>
  </Stack>
</>

const ResetPasswordReset = () => {
  const { isNewUser, userId, token, info, setIsFinished } = useContext(resetPasswordPageContext)!

  return <>
    <ResetPasswordUserInfo bg='dashboard-header-bg' color='white' {...info!} />
    <ResetPasswordForm mt='auto' isNewUser={isNewUser} userId={userId!} token={token!} info={info!} onPasswordReset={() => setIsFinished(true)} />
  </>
}

const ResetPasswordFinished = () => {
  const { isNewUser } = useResetPasswordContext()

  return <>
    <Center flexGrow={1} as={Stack} py={{ base: 4, md: 6 }} gap={{ base: 4, md: 6 }}>
      <Icon as={FontAwesomeIcon} icon={faCheckCircle} color='green.600' size='4x' />
      <Heading fontSize='4xl' fontWeight='bold' color='green.600'>
        {isNewUser ? 'Heslo nastaveno' : 'Heslo změněno'}
      </Heading>
      <Heading fontSize='lg'>
        Vaše heslo bylo úspěšně nastaveno. Nyní se můžete přihlásit a&nbsp;začít používat aplikaci.
      </Heading>
    </Center>
    <Button as={Link} to="/" size='lg'>Přejít na přihlášení</Button>
  </>
}

const ResetPasswordError = () => {
  return <>
    <Center flexGrow={1} as={Stack} gap={{ base: 4, md: 8 }}>
      <Icon as={FontAwesomeIcon} icon={faCircleXmark} color='red.700' size='4x' />
      <Heading fontSize='4xl' fontWeight='bold' color='red.700'>
        Odkaz je neplatný
      </Heading>
      <Text fontSize='lg'>
        Požádejte si o&nbsp;nový odkaz a&nbsp;zkuste to znovu. Pokud problém přetrvává, kontaktujte podporu.
      </Text>
    </Center>
    <Divider />
    <SendResetPasswordLinkForm mt='auto' w='full' />
  </>
}